import React, { FC } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Paper } from "@mui/material";
import { useQuery } from "react-query";
import { BeatLoader } from "react-spinners";
import Image from "next/image";
import { ORGANISATIONS_AUTOCOMPLETE_LIMIT } from "@config/config.client";

interface Props {
  id: string;
  cN?: string;
  value: any;
  setValue: (value: any) => void;
}

interface Option {
  id: string;
  name: string;
}

export const AsyncAutocompleteOrganisations: FC<Props> = ({
  id,
  cN,
  value,
  setValue,
}) => {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly Option[]>([]);
  const [searchValue, setSearchValue] = React.useState<string>("");

  // Fetch organisations. (based on search params)
  const fetchOrganisations = async () => {
    const url = `/api/organisations/search?name=${searchValue}`;
    return fetch(url).then((res) => res.json());
  };

  const { isLoading } = useQuery(
    ["organisations", searchValue],
    fetchOrganisations,
    {
      onSuccess: (data) => {
        const sortedOptions = data.organisations.sort((a, b) =>
          a.name < b.name ? -1 : a.name > b.name ? 1 : 0
        );
        const sortedOptionsWithAddNewOrganisation = [
          ...sortedOptions,
          {
            id: "",
            name: `Add "${searchValue}"`,
            isAddNew: true,
            searchValue,
          },
        ];
        const newOptions =
          data.length === ORGANISATIONS_AUTOCOMPLETE_LIMIT
            ? sortedOptions
            : sortedOptionsWithAddNewOrganisation;
        setOptions(newOptions);
      },
    }
  );

  return (
    <Autocomplete
      multiple={false}
      id={id}
      classes={{
        inputFocused: "focus:ring-0",
        listbox: "border",
      }}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      value={value}
      onChange={(_, value) => {
        if (value?.isAddNew) {
          setValue({
            id: "",
            name: value.searchValue,
          });
        } else {
          setValue(value);
        }
      }}
      options={options}
      //   groupBy={(option) => option.country}
      //   noOptionsText={() => (
      //     <div
      //       onClick={() => {
      //         console.log("clicked");
      //       }}
      //     >
      //       Create new
      //     </div>
      //   )}
      freeSolo={true}
      getOptionLabel={(option) => option.name || ""}
      renderOption={(props, option) => (
        <li {...props} key={option.id} className="flex p-2 hover:bg-gray-100">
          {option.favicon ? (
            <Image
              className="mr-4 rounded w-5 h-5"
              width={18}
              height={18}
              src={`${process.env.NEXT_PUBLIC_SUPABASE_PROJECT_URL}/storage/v1/object/public/public${option?.favicon}`}
              alt={`${option?.name || ""} logo`}
            />
          ) : null}
          <span>{option.name}</span>
        </li>
      )}
      isOptionEqualToValue={(option, value) => {
        return option === value;
      }}
      loading={isLoading}
      filterSelectedOptions
      PaperComponent={({ children }) => (
        <Paper
          // This is to work together with SearchFields UI, specifically not to trigger onclickoutside which would close the UI instead of selecting the value
          // More: https://www.npmjs.com/package/react-cool-onclickoutside#user-content-ignore-elements-by-css-class-name
          className="ignore-onclickoutside"
        >
          {children}
        </Paper>
      )}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            placeholder="Company name"
            className=""
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? (
                    <BeatLoader size={8} margin={2} color="#aaa" />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        );
      }}
    />
  );
};
